import { Wind, FlaskConical, Droplets } from 'lucide-react';
import EquipmentAdminValidation from '@/components/EquipmentAdminValidation';

export default function AdminAerationValidation() {
  return (
    <EquipmentAdminValidation
      title="Aeration Validation — Admin Audit"
      description="Activated sludge with Monod kinetics, OUR, oxygen transfer, and nitrification physics."
      buildSections={(p) => {
        const flowRate = Number(p.flowRate ?? 250);
        const influentBOD = Number(p.bod ?? 1850);
        const influentCOD = Number(p.cod ?? 3200);
        const influentTSS = Number(p.tss ?? 450);
        const influentNH3 = Number(p.nh3n ?? 45);
        const temperature = Number(p.temp ?? 25);
        const tankLength = 12, tankWidth = 6, tankDepth = 4.5;
        const mlssTarget = 3500;
        const alphaFactor = 0.85;
        const yieldCoeff = 0.6;
        const decayRate = 0.06;
        const halfSaturation = 50;
        const maxGrowthRate = 3.5;
        const nitrificationEnabled = true;
        const nitrificationRate = 0.35;

        const Q = flowRate;
        const q = Q / 24;
        const tankArea = tankLength * tankWidth;
        const workingVolume = tankArea * tankDepth;
        const detention = workingVolume / q;
        const hlr = q / tankArea;
        const T = temperature;
        const tempK = T + 273.15;
        const muWater = 0.001 * Math.exp(-1.5 * (T - 20) / tempK);
        const rhoWater = 998 - 0.2 * (T - 20);
        const S0 = influentBOD;
        const X = mlssTarget;
        const mu = maxGrowthRate * S0 / (halfSaturation + S0);
        const muCorrected = mu * Math.pow(1.07, T - 20);
        const U = (muCorrected / yieldCoeff) + decayRate;
        const fmRatio = (S0 * Q / 1000) / (X * workingVolume / 1000);
        const srt = 1 / Math.max(0.001, yieldCoeff * U - decayRate);
        const S_eff = halfSaturation * decayRate / (yieldCoeff * muCorrected - decayRate);
        const bodRemoval = Math.min(0.98, Math.max(0.1, 1 - S_eff / S0));
        const effluentBOD = S0 * (1 - bodRemoval);
        const codRemoval = bodRemoval * 0.92;
        const effluentCOD = influentCOD * (1 - codRemoval);
        const tssRemoval = Math.min(0.95, bodRemoval * 0.95);
        const effluentTSS = influentTSS * (1 - tssRemoval);
        const mu_nit = nitrificationRate * Math.pow(1.07, T - 20) * influentNH3 / (1 + influentNH3);
        const nit_removal = Math.min(0.99, 1 - 1 / (mu_nit * srt));
        const nitrificationEfficiency = nit_removal;
        const effluentNH3 = influentNH3 * (1 - nit_removal);
        const biomassInventory = X * workingVolume / 1000;
        const biomassProduction = yieldCoeff * (S0 - effluentBOD) * Q / 1000;
        const a_prime = 0.5;
        const b_prime = 0.1;
        const our_mg_L_h = a_prime * (S0 - effluentBOD) / 24 + b_prime * X / 24;
        const sor = our_mg_L_h * workingVolume / 1000 * 24;
        const sote = 0.25 * alphaFactor * Math.pow(1.02, 20 - T) * (tankDepth / 4);
        const sotr = sor / Math.max(sote, 0.05);
        const airDensity = 1.2;
        const o2InAir = 0.232;
        const airFlowRate = (sotr / 24 / 60) / (airDensity * o2InAir);
        const diffuserFlow = 2;
        const diffuserCount = Math.ceil((airFlowRate * 60) / diffuserFlow);
        const deltaP = 50000;
        const etaBlower = 0.75;
        const blowerPower = (airFlowRate / 60 * deltaP) / (etaBlower * 1000);
        const mixerPower = 0.02 * workingVolume;
        const totalPower = blowerPower + mixerPower;

        const fmt = (v: any, d = 2) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

        return [
          {
            title: 'Tank Physics',
            icon: Wind,
            equations: [
              { label: 'Tank area', formula: 'A = L × W', substitution: `A = ${tankLength} × ${tankWidth} = ${fmt(tankArea, 0)} m²`, result: fmt(tankArea, 0), status: 'pass', unit: 'm²' },
              { label: 'Working volume', formula: 'V = A × depth', substitution: `V = ${fmt(tankArea, 0)} × ${tankDepth} = ${fmt(workingVolume, 0)} m³`, result: fmt(workingVolume, 0), status: 'pass', unit: 'm³' },
              { label: 'Detention time', formula: 'τ = V / (Q/24)', substitution: `τ = ${fmt(workingVolume, 0)} / ${fmt(q, 1)} = ${fmt(detention, 1)} h`, result: fmt(detention, 1), status: detention >= 4 ? 'pass' : 'warn', unit: 'h' },
              { label: 'HLR', formula: 'HLR = (Q/24) / A', substitution: `HLR = ${fmt(q, 1)} / ${fmt(tankArea, 0)} = ${fmt(hlr, 2)} m³/m²·h`, result: fmt(hlr, 2), status: 'pass', unit: 'm³/m²·h' },
            ],
          },
          {
            title: 'Monod Kinetics & Biomass',
            icon: FlaskConical,
            equations: [
              { label: 'Growth rate μ', formula: 'μ = μmax × S / (Ks + S)', substitution: `μ = ${maxGrowthRate} × ${S0} / (${halfSaturation} + ${S0}) = ${fmt(mu, 2)} d⁻¹`, result: fmt(mu, 2), status: 'pass', unit: 'd⁻¹' },
              { label: 'Temp-corrected μ', formula: 'μ_T = μ × 1.07^(T−20)', substitution: `μ_T = ${fmt(mu, 2)} × 1.07^${T - 20} = ${fmt(muCorrected, 2)} d⁻¹`, result: fmt(muCorrected, 2), status: 'pass', unit: 'd⁻¹' },
              { label: 'Substrate utilization U', formula: 'U = (μ_T/Y) + kd', substitution: `U = (${fmt(muCorrected, 2)}/${yieldCoeff}) + ${decayRate} = ${fmt(U, 3)}`, result: fmt(U, 3), status: 'pass', unit: '' },
              { label: 'F/M ratio', formula: 'F/M = (S0×Q/1000) / (X×V/1000)', substitution: `F/M = (${S0}×${Q}/1000) / (${X}×${fmt(workingVolume, 0)}/1000) = ${fmt(fmRatio, 2)}`, result: fmt(fmRatio, 2), status: fmRatio >= 0.05 && fmRatio <= 0.3 ? 'pass' : fmRatio >= 0.02 && fmRatio <= 0.5 ? 'warn' : 'fail', unit: 'kg/kg·d' },
              { label: 'SRT', formula: 'θc = 1 / (Y×U − kd)', substitution: `θc = 1 / (${yieldCoeff}×${fmt(U, 3)} − ${decayRate}) = ${fmt(srt, 1)} d`, result: fmt(srt, 1), status: srt >= 3 ? 'pass' : srt >= 2 ? 'warn' : 'fail', unit: 'days' },
              { label: 'BOD removal', formula: 'η = 1 − S_eff/S0', substitution: `η = 1 − ${fmt(S_eff, 1)}/${S0} = ${fmt(bodRemoval * 100, 0)}%`, result: fmt(bodRemoval * 100, 0), status: bodRemoval >= 0.85 ? 'pass' : bodRemoval >= 0.70 ? 'warn' : 'fail', unit: '%' },
              { label: 'COD removal', formula: 'η_COD = η_BOD × 0.92', substitution: `η_COD = ${fmt(bodRemoval * 100, 0)}% × 0.92 = ${fmt(codRemoval * 100, 0)}%`, result: fmt(codRemoval * 100, 0), status: 'pass', unit: '%' },
              { label: 'TSS removal', formula: 'η_TSS = η_BOD × 0.95', substitution: `η_TSS = ${fmt(bodRemoval * 100, 0)}% × 0.95 = ${fmt(tssRemoval * 100, 0)}%`, result: fmt(tssRemoval * 100, 0), status: 'pass', unit: '%' },
              { label: 'Biomass inventory', formula: 'X×V/1000', substitution: `${X}×${fmt(workingVolume, 0)}/1000 = ${fmt(biomassInventory, 0)} kg`, result: fmt(biomassInventory, 0), status: 'pass', unit: 'kg' },
              { label: 'Biomass production', formula: 'Y×(S0−S)×Q/1000', substitution: `${yieldCoeff}×(${S0}−${fmt(effluentBOD, 1)})×${Q}/1000 = ${fmt(biomassProduction, 1)} kg/d`, result: fmt(biomassProduction, 1), status: 'pass', unit: 'kg/d' },
            ],
          },
          {
            title: 'Nitrification',
            icon: FlaskConical,
            equations: [
              { label: 'Nitrifier growth', formula: 'μ_nit = μ_nit,max × 1.07^(T−20) × NH3/(1+NH3)', substitution: `μ_nit = ${nitrificationRate} × 1.07^${T - 20} × ${influentNH3}/${1 + influentNH3} = ${fmt(mu_nit, 3)} d⁻¹`, result: fmt(mu_nit, 3), status: 'pass', unit: 'd⁻¹' },
              { label: 'Nitrification efficiency', formula: 'η = 1 − 1/(μ_nit × θc)', substitution: `η = 1 − 1/(${fmt(mu_nit, 3)} × ${fmt(srt, 1)}) = ${fmt(nitrificationEfficiency * 100, 0)}%`, result: fmt(nitrificationEfficiency * 100, 0), status: nitrificationEfficiency >= 0.70 ? 'pass' : 'warn', unit: '%' },
              { label: 'Effluent NH₃', formula: 'NH3_out = NH3_in × (1 − η)', substitution: `${influentNH3} × (1 − ${fmt(nitrificationEfficiency, 2)}) = ${fmt(effluentNH3, 1)} mg/L`, result: fmt(effluentNH3, 1), status: effluentNH3 <= 5 ? 'pass' : 'warn', unit: 'mg/L' },
            ],
          },
          {
            title: 'Oxygen Transfer & Power',
            icon: Droplets,
            equations: [
              { label: 'OUR', formula: 'OUR = 0.5×(S0−S)/24 + 0.1×X/24', substitution: `OUR = 0.5×(${S0}−${fmt(effluentBOD, 1)})/24 + 0.1×${X}/24 = ${fmt(our_mg_L_h, 1)} mg/L·h`, result: fmt(our_mg_L_h, 1), status: 'pass', unit: 'mg/L·h' },
              { label: 'SOR', formula: 'SOR = OUR × V/1000 × 24', substitution: `SOR = ${fmt(our_mg_L_h, 1)} × ${fmt(workingVolume, 0)}/1000 × 24 = ${fmt(sor, 1)} kg O₂/day`, result: fmt(sor, 1), status: 'pass', unit: 'kg/day' },
              { label: 'SOTE', formula: 'SOTE = 0.25×α×1.02^(20−T)×(depth/4)', substitution: `SOTE = 0.25×${alphaFactor}×1.02^${20 - T}×(${tankDepth}/4) = ${fmt(sote, 3)}`, result: fmt(sote, 3), status: 'pass', unit: '' },
              { label: 'SOTR', formula: 'SOTR = SOR / SOTE', substitution: `SOTR = ${fmt(sor, 1)} / ${fmt(sote, 3)} = ${fmt(sotr, 1)} kg/day`, result: fmt(sotr, 1), status: 'pass', unit: 'kg/day' },
              { label: 'Air flow rate', formula: 'Q_air = (SOTR/24/60) / (ρ_air × 0.232)', substitution: `Q_air = (${fmt(sotr, 1)}/24/60) / (1.2 × 0.232) = ${fmt(airFlowRate, 1)} m³/min`, result: fmt(airFlowRate, 1), status: 'pass', unit: 'm³/min' },
              { label: 'Diffuser count', formula: 'N = ceil(Q_air×60 / 2)', substitution: `N = ceil(${fmt(airFlowRate, 1)}×60 / 2) = ${diffuserCount}`, result: String(diffuserCount), status: 'pass', unit: '' },
              { label: 'Blower power', formula: 'P = (Q_air/60 × ΔP) / (η × 1000)', substitution: `P = (${fmt(airFlowRate, 1)}/60 × 50000) / (0.75 × 1000) = ${fmt(blowerPower, 2)} kW`, result: fmt(blowerPower, 2), status: 'pass', unit: 'kW' },
              { label: 'Mixer power', formula: 'P = 0.02 × V', substitution: `P = 0.02 × ${fmt(workingVolume, 0)} = ${fmt(mixerPower, 2)} kW`, result: fmt(mixerPower, 2), status: 'pass', unit: 'kW' },
              { label: 'Total power', formula: 'P_total = P_blower + P_mixer', substitution: `P_total = ${fmt(blowerPower, 2)} + ${fmt(mixerPower, 2)} = ${fmt(totalPower, 2)} kW`, result: fmt(totalPower, 2), status: 'pass', unit: 'kW' },
            ],
          },
        ];
      }}
    />
  );
}
